import React, { useMemo, useState, useEffect, useCallback, useDeferredValue, memo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
    Container, Box, Card, CardHeader, CardContent, Typography, Button, Stack,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
    TextField, Alert, CircularProgress, Divider, Autocomplete
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SaveIcon from "@mui/icons-material/Save";
import ReplayIcon from "@mui/icons-material/Replay";
import RefreshIcon from "@mui/icons-material/Refresh";

const DEBUG = true;
const dlog = (...a) => DEBUG && console.log("[PlanUpload]", ...a);

// Acceptation libre : alphanumérique ou numérique, juste non-vide (la normalisation
// finale est faite côté backend par normCompteSmart : strip zéros initiaux si purement
// numérique, sinon préserve l'alphanumérique tel quel).
const isCompteOK = (s) => String(s || "").trim().length > 0;
const isLibOK = (s) => String(s || "").trim().length > 0;

// ---------- Ligne optimisée (mémoïsée) ----------
const PreviewRow = memo(function PreviewRow({ row, idx, invalidsByKey, updateCell }) {
    const serverKey = row?.sourceRow ? `row:${row.sourceRow}` : (row?.compte ? `c:${row.compte}` : null);
    const serverErr = serverKey ? (invalidsByKey.get(serverKey) || "") : "";

    const localErr = useMemo(() => {
        const c = String(row?.compte ?? "").trim();
        const l = String(row?.libelle ?? "").trim();
        if (!isCompteOK(c)) return "Compte vide";
        if (!isLibOK(l)) return "Libellé vide";
        return null;
    }, [row]);

    const showCompteErr = !!localErr?.includes("Compte") || /compte/i.test(serverErr);
    const showLibErr = !!localErr?.includes("Libellé") || /libell/i.test(serverErr);

    const onCompteChange = (e) => updateCell(idx, "compte", e.target.value);
    const onLibCommit = (val) => updateCell(idx, "libelle", val);

    return (
        <TableRow
            hover
            sx={{ "&:nth-of-type(odd)": { bgcolor: "action.hover" } }}
            selected={showCompteErr || showLibErr}
        >
            <TableCell sx={{ color: "text.secondary" }}>
                Ligne {row?.sourceRow ?? idx + 1}
            </TableCell>

            {/* Compte : contrôlé, mais la ligne est mémoïsée */}
            <TableCell>
                <TextField
                    label="Compte"
                    size="small"
                    fullWidth
                    value={row.compte ?? ""}
                    onChange={onCompteChange}
                    inputProps={{ style: { fontFamily: "monospace" }, "aria-label": "Compte", autoComplete: "off" }}
                    error={showCompteErr}
                    helperText={showCompteErr ? (serverErr || localErr) : null}
                    variant="outlined"
                />
            </TableCell>

            {/* Libellé : NON contrôlé → commit au blur/Enter */}
            <TableCell>
                <TextField
                    label="Libellé"
                    size="small"
                    fullWidth
                    defaultValue={row.libelle ?? ""}
                    onBlur={(e) => onLibCommit(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            onLibCommit(e.currentTarget.value);
                            e.currentTarget.blur();
                        }
                    }}
                    inputProps={{ "aria-label": "Libellé", autoComplete: "off" }}
                    error={showLibErr}
                    helperText={showLibErr ? (serverErr || localErr) : null}
                    variant="outlined"
                />
            </TableCell>
        </TableRow>
    );
});

export default function PlanComptableImport() {
    const { clientId } = useParams();

    const [file, setFile] = useState(null);
    const [previewRows, setPreviewRows] = useState([]);
    const [serverInvalids, setServerInvalids] = useState([]);
    const [serverIgnored, setServerIgnored] = useState([]);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [successMsg, setSuccessMsg] = useState("");
    const [finalRows, setFinalRows] = useState([]);
    const [loadingFinal, setLoadingFinal] = useState(false);

    // Sélection client
    const [clients, setClients] = useState([]);
    const [selectedClientId, setSelectedClientId] = useState(clientId || "");
    const [loadingClients, setLoadingClients] = useState(false);

    const effectiveClientId = clientId || selectedClientId;

    const getClientLabel = (c) =>
        (c?.raisonSociale && c.raisonSociale.trim()) ||
        `${c?.prenom || ""} ${c?.nom || ""}`.trim() ||
        c?.name ||
        c?._id ||
        "—";

    const invalidsByKey = useMemo(() => {
        const map = new Map();
        (serverInvalids || []).forEach((x) => {
            const key =
                x?.row && Number.isFinite(+x.row)
                    ? `row:${x.row}`
                    : x?.compte
                        ? `c:${String(x.compte)}`
                        : null;
            if (key) map.set(key, x?.reason || "invalide");
        });
        return map;
    }, [serverInvalids]);

    // Chargement clients (si pas de clientId dans l’URL)
    useEffect(() => {
        if (!clientId) {
            const fetchClients = async () => {
                setLoadingClients(true);
                try {
                    const { data } = await axios.get("/api/comptable/clients", { params: { limit: 1000 } });
                    const arr = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
                    setClients(arr);
                } catch (err) {
                    setErrorMsg(
                        err?.response?.data?.error?.message ||
                        err?.message ||
                        "Erreur lors du chargement des clients"
                    );
                    dlog("clients fetch error:", err);
                } finally {
                    setLoadingClients(false);
                }
            };
            fetchClients();
        }
    }, [clientId]);

    // Rafraîchir plan existant quand client change
    useEffect(() => {
        if (effectiveClientId) {
            refreshFinalFromDB();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveClientId]);

    // ----------- Actions -----------
    const previewFromFile = async (selectedFile) => {
        setErrorMsg("");
        setSuccessMsg("");
        if (!effectiveClientId) {
            setErrorMsg("Veuillez sélectionner un client.");
            return;
        }
        if (!selectedFile) {
            setErrorMsg("Choisissez un fichier .xls/.xlsx/.csv.");
            return;
        }

        setLoadingPreview(true);
        try {
            const form = new FormData();
            form.append("file", selectedFile);

            const url = `/api/comptable/clients/${effectiveClientId}/plan/import`;
            dlog("POST (file-auto) ->", url);

            const { data } = await axios.post(url, form, { params: { dryRun: true } });

            const rows = data?.data?.previewRows ?? [];
            setPreviewRows(rows);
            const srvInvalids = data?.meta?.invalids ?? data?.invalids ?? [];
            const srvIgnored = data?.meta?.ignored ?? data?.ignored ?? [];
            setServerInvalids(srvInvalids);
            setServerIgnored(srvIgnored);
            setFinalRows([]);
        } catch (err) {
            const msg =
                err?.response?.data?.error?.message || err?.message || "Erreur chargement preview";
            setErrorMsg(msg);
            dlog("preview error:", err);
        } finally {
            setLoadingPreview(false);
        }
    };

    const handlePreview = async () => {
        await previewFromFile(file);
    };

    const handleSave = async () => {
        setErrorMsg("");
        setSuccessMsg("");
        if (!effectiveClientId) {
            setErrorMsg("Veuillez sélectionner un client.");
            return;
        }
        if (previewRows.length === 0) {
            setErrorMsg("Aucune ligne à enregistrer.");
            return;
        }

        // Validation minimale côté client (évite un aller-retour si évident)
        const badIndex = previewRows.findIndex((r) => {
            const c = String(r?.compte ?? "").trim();
            const l = String(r?.libelle ?? "").trim();
            if (!isCompteOK(c)) return true;
            if (!isLibOK(l)) return true;
            return false;
        });
        if (badIndex !== -1) {
            setErrorMsg(
                `Corrigez les erreurs de la ligne ${previewRows[badIndex].sourceRow ?? badIndex + 1} avant d'enregistrer.`
            );
            return;
        }

        setSaving(true);
        try {
            const url = `/api/comptable/clients/${effectiveClientId}/plan/import`;

            // Dry-run JSON
            dlog("POST (dry-run JSON) ->", url);
            const { data: dry } = await axios.post(url, { rows: previewRows }, { params: { dryRun: true } });
            const invalids = dry?.meta?.invalids ?? dry?.invalids ?? [];
            setServerInvalids(invalids);
            setServerIgnored(dry?.meta?.ignored ?? dry?.ignored ?? []);
            if (invalids.length > 0) {
                setErrorMsg(
                    `Des lignes invalides sont détectées par le serveur (${invalids.length}). Corrigez puis réessayez.`
                );
                setSaving(false);
                return;
            }

            // Commit JSON
            dlog("POST (commit JSON) ->", url);
            const { data: commit } = await axios.post(
                url,
                { rows: previewRows },
                { params: { dryRun: false } }
            );

            const created = commit?.data?.created ?? 0;
            const updated = commit?.data?.updated ?? 0;
            const deleted = commit?.data?.deleted ?? 0;

            // Récupérer l’état final
            let finalItems = commit?.data?.finalItems ?? [];
            if (!Array.isArray(finalItems) || finalItems.length === 0) {
                const { data: finalData } = await axios.get(
                    `/api/comptable/clients/${effectiveClientId}/plan`,
                    { params: { limit: 5000, sort: "compte", order: "asc", numeric: true } }
                );
                finalItems = Array.isArray(finalData) ? finalData : (finalData?.data ?? []);
            }
            setFinalRows(finalItems);

            setSuccessMsg(
                `Enregistré avec succès. Créés: ${created} | Mis à jour: ${updated} | Supprimés: ${deleted}`
            );

            setFile(null);
            setPreviewRows([]);
            setServerInvalids([]);
            setServerIgnored([]);
        } catch (err) {
            const msg = err?.response?.data?.error?.message || err?.message || "Erreur enregistrement";
            setErrorMsg(msg);
            dlog("save error:", err);
        } finally {
            setSaving(false);
        }
    };

    const resetAll = () => {
        setErrorMsg("");
        setSuccessMsg("");
        setFile(null);
        setPreviewRows([]);
        setServerInvalids([]);
        setServerIgnored([]);
        // on garde finalRows pour voir le plan existant
    };

    const refreshFinalFromDB = async () => {
        if (!effectiveClientId) return;
        setLoadingFinal(true);
        try {
            const { data } = await axios.get(`/api/comptable/clients/${effectiveClientId}/plan`, {
                params: { limit: 5000, sort: "compte", order: "asc", numeric: true }
            });
            const rows = Array.isArray(data) ? data : (data?.data ?? []);
            setFinalRows(rows);
        } catch (e) {
            setErrorMsg(e?.response?.data?.error?.message || e?.message || "Erreur rafraîchissement");
        } finally {
            setLoadingFinal(false);
        }
    };

    // ----------- Optimisations de rendu -----------
    const updateCell = useCallback((index, field, value) => {
        setPreviewRows((prev) => {
            const next = prev.slice();
            const cur = next[index] || {};
            if (cur[field] === value) return prev;
            next[index] = { ...cur, [field]: value };
            return next;
        });
    }, []);

    const deferredPreviewRows = useDeferredValue(previewRows);

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Card elevation={3} sx={{ borderRadius: 2 }}>
                <CardHeader
                    title="Importer un Plan Comptable "
                    titleTypographyProps={{ variant: "h5" }}
                    subheaderTypographyProps={{ variant: "body1", color: "text.secondary" }}
                    sx={{ bgcolor: "background.default", pb: 2 }}
                />
                <CardContent sx={{ px: 3, py: 2 }}>
                    {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
                    {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}

                    {serverIgnored?.length > 0 && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {serverIgnored.length} ligne(s) ignorée(s) (totaux/entêtes…). Exemple : {serverIgnored[0]?.libelle ?? "—"}
                        </Alert>
                    )}

                    {!clientId && (
                        <Autocomplete
                            fullWidth
                            sx={{ mb: 3, maxWidth: 480 }}
                            options={clients}
                            value={clients.find(c => c._id === selectedClientId) || null}
                            onChange={(_, newValue) => {
                                setSelectedClientId(newValue?._id || "");
                                setFile(null);
                                setPreviewRows([]);
                                setServerInvalids([]);
                                setServerIgnored([]);
                            }}
                            getOptionLabel={(option) => {
                                const name = getClientLabel(option);
                                const code = option.codeClient || '';
                                return code ? `${name} (${code})` : name;
                            }}
                            filterOptions={(options, { inputValue }) => {
                                const search = inputValue.toLowerCase().trim();
                                if (!search) return options;
                                return options.filter((c) => {
                                    const name = getClientLabel(c).toLowerCase();
                                    const code = (c.codeClient || '').toLowerCase();
                                    return name.includes(search) || code.includes(search);
                                });
                            }}
                            loading={loadingClients}
                            noOptionsText="Aucun client disponible"
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Sélectionner un client"
                                    placeholder="Rechercher par nom ou code client..."
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingClients ? <CircularProgress size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option._id === value?._id}
                        />
                    )}

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" sx={{ mb: 3 }}>
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<CloudUploadIcon />}
                            aria-label="Sélectionner un fichier Excel"
                            sx={{ minWidth: 180 }}
                            disabled={!effectiveClientId || loadingClients}
                        >
                            Sélectionner fichier
                            <input
                                hidden
                                type="file"
                                accept=".xls,.xlsx,.csv"
                                onChange={(e) => {
                                    const f = e.target.files?.[0] || null;
                                    setFile(f);
                                    if (f) previewFromFile(f);
                                }}
                            />
                        </Button>
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 420, flex: 1 }}>
                            {file ? file.name : "Aucun fichier sélectionné"}
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={handlePreview}
                            disabled={!file || loadingPreview || !effectiveClientId}
                            startIcon={<ReplayIcon />}
                            aria-label="Re-générer la prévisualisation"
                            sx={{ minWidth: 220 }}
                        >
                            {loadingPreview ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                            {loadingPreview ? "Analyse en cours..." : "Re-générer prévisualisation"}
                        </Button>
                        <Button
                            variant="text"
                            startIcon={<ReplayIcon />}
                            onClick={resetAll}
                            disabled={loadingPreview || saving || !effectiveClientId}
                            aria-label="Réinitialiser"
                        >
                            Réinitialiser
                        </Button>
                    </Stack>

                    {previewRows.length > 0 ? (
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                Prévisualisation éditable ({previewRows.length} lignes)
                            </Typography>
                            <TableContainer
                                component={Paper}
                                elevation={1}
                                sx={{ maxHeight: 500, borderRadius: 1, border: "1px solid", borderColor: "divider" }}
                            >
                                <Table size="small" stickyHeader aria-label="Tableau de prévisualisation">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: "background.paper", fontWeight: "bold" }} width={120}>
                                                Source
                                            </TableCell>
                                            <TableCell sx={{ bgcolor: "background.paper", fontWeight: "bold" }} width={220}>
                                                Compte
                                            </TableCell>
                                            <TableCell sx={{ bgcolor: "background.paper", fontWeight: "bold" }}>
                                                Libellé
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {deferredPreviewRows.map((row, idx) => (
                                            <PreviewRow
                                                key={row.sourceRow ?? idx}
                                                row={row}
                                                idx={idx}
                                                invalidsByKey={invalidsByKey}
                                                updateCell={updateCell}
                                            />
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<SaveIcon />}
                                    onClick={handleSave}
                                    disabled={saving || !effectiveClientId}
                                    aria-label="Enregistrer en base"
                                    sx={{ px: 4 }}
                                >
                                    {saving ? <CircularProgress size={18} sx={{ mr: 1, color: "inherit" }} /> : null}
                                    {saving ? "Enregistrement en cours..." : "Enregistrer"}
                                </Button>
                            </Stack>
                        </Box>
                    ) : null}

                    {previewRows.length === 0 && (
                        <>
                            {loadingFinal && <Alert severity="info" sx={{ mb: 3 }}>Chargement du plan existant…</Alert>}

                            {finalRows.length > 0 ? (
                                <>
                                    <Divider sx={{ my: 2 }} />
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        sx={{ mb: 2 }}
                                    >
                                        <Typography variant="h6">
                                            Plan Comptable en Base de Données ({finalRows.length} lignes)
                                        </Typography>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<RefreshIcon />}
                                            onClick={refreshFinalFromDB}
                                            disabled={loadingFinal || !effectiveClientId}
                                            aria-label="Rafraîchir depuis la base"
                                        >
                                            {loadingFinal ? "Rafraîchissement…" : "Rafraîchir"}
                                        </Button>
                                    </Stack>
                                    <TableContainer
                                        component={Paper}
                                        elevation={1}
                                        sx={{ maxHeight: 500, borderRadius: 1, border: "1px solid", borderColor: "divider" }}
                                    >
                                        <Table size="small" stickyHeader aria-label="Tableau du plan comptable final">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ bgcolor: "background.paper", fontWeight: "bold" }} width={220}>
                                                        Compte
                                                    </TableCell>
                                                    <TableCell sx={{ bgcolor: "background.paper", fontWeight: "bold" }}>
                                                        Libellé
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {finalRows.map((r, i) => (
                                                    <TableRow
                                                        key={r._id ?? i}
                                                        hover
                                                        sx={{ "&:nth-of-type(odd)": { bgcolor: "action.hover" } }}
                                                    >
                                                        <TableCell sx={{ fontFamily: "monospace" }}>{r.compte}</TableCell>
                                                        <TableCell>{r.libelle}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            ) : (
                                !loadingFinal && (
                                    <Alert severity="info" sx={{ mb: 3 }}>
                                        {effectiveClientId
                                            ? "Importez un fichier Excel pour commencer."
                                            : "Veuillez sélectionner un client pour voir ou importer un plan comptable."}
                                    </Alert>
                                )
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
        </Container>
    );
}
